/**
 * /music-stand — hands-free page turns from the front camera.
 *
 * Camera through CameraManager (it explains every refusal in the person's
 * words and next to the button that asked), face through FRLandmarks (the
 * site's MediaPipe ladder: CPU delegate first, a probe after creation,
 * recovery on the next candidate, FRDetectorUnavailable when none is left).
 * The decisions — calibration, thresholds, hysteresis — are gesture-core.js.
 *
 * Cost, said out loud in the panel: the face model (~4 MB, once) runs on
 * this device's processor about 12–15 times a second while turns are on.
 * The loop stops when the tab is hidden and when the stand is closed.
 */
import { KINDS, signalOf, calibrate, thresholdFor, createDetector } from './gesture-core.js';

const D = 180 / Math.PI;

/** Head yaw in degrees: from MediaPipe's transformation matrix, or from the nose between the cheeks. */
export function yawOf(face) {
    const m = face && face.m;
    if (m && m.length >= 11) return Math.atan2(-m[2], Math.hypot(m[0], m[1])) * D;
    const pts = face && face.pts;
    if (!pts || !pts[1] || !pts[234] || !pts[454]) return NaN;
    const l = pts[234].x, r = pts[454].x;
    if (Math.abs(r - l) < 1e-3) return NaN;
    // same sign as the matrix: positive when the face turns to the frame's right
    return ((pts[1].x - l) / (r - l) - 0.5) * 110;
}

export function createGestures(S, R) {
    const E = S.els;
    const G = {
        raw: NaN,
        on: false, cam: null, stream: null, loopT: 0, busy: false, det: null,
        phase: 'off', cal: null, samples: null, lastFace: 0, fps: 0, frames: 0, fpsT: 0, fires: 0, startedAt: 0,
        detectMs: 0,
    };
    const kind = () => (KINDS[S.settings.gKind] ? S.settings.gKind : 'head');

    function status(text, tone) {
        E.gStatus.textContent = text || '';
        E.gStatus.dataset.tone = tone || '';
    }

    function setPhase(p) {
        G.phase = p;
        E.gPanel.dataset.phase = p;
        E.gDot.dataset.state = p === 'off' ? 'off' : 'wait';
        E.gDot.hidden = p === 'off';
        E.gOn.textContent = S.t(p === 'off' ? 'g_on' : 'g_off');
        E.gOn.setAttribute('aria-pressed', p === 'off' ? 'false' : 'true');
        E.gCalib.disabled = !(p === 'ready' || p === 'calibrating');
    }

    function buildDetector() {
        const k = kind();
        const cal = (S.settings.gCal || {})[k];
        if (!cal || !cal.ok) { G.det = null; return false; }
        G.det = createDetector({ kind: k, base: cal.base, sign: cal.sign, threshold: thresholdFor(k, cal, S.settings.gSens) });
        return true;
    }
    G.rebuild = () => { buildDetector(); drawMeter(); };

    async function enable(btn) {
        if (G.on) return;
        G.on = true;
        setPhase('starting');
        status(S.t('g_asking_camera'));
        const CM = window.CameraManager;
        if (!CM) { fail(S.t('g_no_camera_module')); return; }
        G.cam = new CM({ slug: 'music-stand', locale: S.locale });
        try {
            G.stream = await G.cam.startRaw({
                video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15, max: 30 } },
                audio: false,
            }, { button: btn });
        } catch (e) {
            const code = (e && e.code) || (CM.classify ? CM.classify(e) : 'generic');
            // the door already spoke next to the button; the panel keeps the sentence too
            fail(G.cam.getErrorMessage ? G.cam.getErrorMessage(code) : S.t('g_camera_failed'), code);
            return;
        }
        if (!G.on) { stopCam(); return; }
        const v = E.gVideo;
        v.srcObject = G.stream;
        v.muted = true; v.playsInline = true;
        try { await v.play(); } catch (_) { /* autoplay of a muted stream is allowed; ignore */ }
        // first-frame guard: a resolved getUserMedia is not a running camera
        const t0 = performance.now();
        while (!(v.videoWidth > 0) && performance.now() - t0 < 6000) await new Promise((r) => setTimeout(r, 100));
        if (!(v.videoWidth > 0)) { fail(S.t('g_no_frames'), 'no_frames'); return; }

        const FR = window.FRLandmarks;
        if (!FR) { fail(S.t('g_model_failed'), 'fr_missing'); return; }
        status(S.t('g_loading_model'));
        const off = FR.onProgress ? FR.onProgress((p) => {
            if (p && p.phase === 'model' && p.total > 0) status(S.t('g_loading_model_pct', { pct: Math.round(p.loaded / p.total * 100) }));
            else if (p && p.phase === 'init') status(S.t('g_preparing'));
        }) : null;
        try {
            await FR.ensure(S.cfg.faceSources || [], { numFaces: 1, mode: 'VIDEO', blendshapes: true, matrix: true });
        } catch (e) {
            if (off) off();
            fail(S.t(FR.isUnavailable && FR.isUnavailable(e) ? 'g_model_unavailable' : 'g_model_failed'), 'fr_unavailable', e);
            return;
        }
        if (off) off();
        if (!G.on) { stopCam(); return; }
        setPhase('ready');
        G.startedAt = performance.now();
        try { window.__stage && window.__stage('gestures_on', { kind: kind() }); } catch (_) { /* optional */ }
        const has = buildDetector();
        status(has ? S.t('g_ready_' + kind()) : S.t('g_need_calibration'), has ? 'ok' : '');
        loop();
        if (!has) startCalibration();
    }

    function fail(text, code, err) {
        status(text, 'bad');
        E.gFail.textContent = text;
        E.gFail.hidden = false;
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'gesture_' + (code || 'failed'), msg: String((err && err.message) || '').slice(0, 100) }); } catch (_) { /* optional */ }
        stopCam();
        G.on = false;
        setPhase('off');
        E.gStatus.textContent = text;
    }

    function stopCam() {
        clearTimeout(G.loopT);
        G.loopT = 0;
        try { if (G.cam) G.cam.stop(); } catch (_) { /* gone */ }
        try { E.gVideo.srcObject = null; } catch (_) { /* gone */ }
        G.stream = null;
    }

    function disable() {
        G.on = false;
        stopCam();
        G.samples = null;
        setPhase('off');
        status(S.t('g_off_note'));
        E.gFail.hidden = true;
        drawMeter();
    }

    /* ── the frame loop ──────────────────────────────────────────────── */

    async function tick() {
        if (!G.on || G.phase === 'off' || G.phase === 'starting') return;
        if (document.hidden) { G.loopT = setTimeout(tick, 400); return; }
        const v = E.gVideo;
        if (!(v.videoWidth > 0) || v.readyState < 2) { G.loopT = setTimeout(tick, 120); return; }
        const t = performance.now();
        let faces = null;
        try {
            faces = await window.FRLandmarks.detect(v, v.videoWidth, v.videoHeight, { timestamp: t });
        } catch (e) {
            const FR = window.FRLandmarks;
            fail(S.t(FR && FR.isUnavailable && FR.isUnavailable(e) ? 'g_model_unavailable' : 'g_model_failed'), 'detect_failed', e);
            return;
        }
        const took = performance.now() - t;
        G.detectMs = G.detectMs ? G.detectMs * 0.9 + took * 0.1 : took;
        G.frames++;
        if (t - G.fpsT > 1000) { G.fps = G.frames * 1000 / (t - G.fpsT || 1); G.frames = 0; G.fpsT = t; }
        const f = faces && faces[0];
        const present = !!f;
        if (present) G.lastFace = t;
        const k = kind();
        const raw = present ? signalOf(k, { yaw: yawOf(f), bs: f.bs || {} }) : NaN;
        E.gDot.dataset.state = present ? 'face' : 'noface';
        if (G.phase === 'calibrating') collect(t, raw, present);
        else if (G.det) {
            const r = G.det.update(t, raw, present);
            if (r) {
                G.fires++;
                E.gDot.dataset.state = 'fire';
                R.turn(r === 'next' ? 1 : -1, 'gesture');
                try { window.__aeFunnel && window.__aeFunnel('gesture_turn', { kind: k, dir: r }); } catch (_) { /* optional */ }
            }
        }
        if (!present && t - G.lastFace > 2500 && G.phase === 'ready') status(S.t('g_no_face'), 'noface');
        else if (present && G.phase === 'ready' && E.gStatus.dataset.tone === 'noface') {
            status(G.det ? S.t('g_ready_' + k) : S.t('g_need_calibration'), G.det ? 'ok' : 'warn');
        }
        G.raw = present ? raw : NaN;
        if (!E.gPanel.hidden) drawPreview(v, f);
        drawMeter(raw, present);
        // 15 frames a second is plenty for a head turn; a slow device gets fewer, never a queue
        const wait = Math.max(20, 66 - took);
        G.loopT = setTimeout(tick, wait);
    }
    function loop() { clearTimeout(G.loopT); G.fpsT = performance.now(); G.frames = 0; G.loopT = setTimeout(tick, 30); }

    /* ── calibration ─────────────────────────────────────────────────── */

    function startCalibration() {
        if (!G.on || !(G.phase === 'ready' || G.phase === 'calibrating')) return;
        setPhase('calibrating');
        G.samples = { step: 'rest', rest: [], move: [], t0: performance.now() };
        E.gCalMsg.hidden = false;
        E.gCalMsg.textContent = S.t('g_cal_rest');
        status(S.t('g_calibrating'));
        try { window.__stage && window.__stage('gesture_calibrate', { kind: kind() }); } catch (_) { /* optional */ }
    }
    G.startCalibration = startCalibration;

    function collect(t, raw, present) {
        const s = G.samples;
        if (!s) return;
        const el = t - s.t0;
        if (s.step === 'rest') {
            if (present) s.rest.push(raw);
            if (el > 1600) {
                s.step = 'move'; s.t0 = t;
                E.gCalMsg.textContent = S.t('g_cal_move_' + kind());
            }
            return;
        }
        if (present) s.move.push(raw);
        if (el > 3200) finishCalibration();
    }

    function finishCalibration() {
        const k = kind();
        const s = G.samples;
        G.samples = null;
        const c = calibrate(k, s.rest, s.move);
        if (!c.ok) {
            E.gCalMsg.textContent = S.t('g_cal_fail_' + (c.reason || 'too_small'));
            setPhase('ready');
            status(S.t('g_need_calibration'), 'warn');
            try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'gesture_cal_' + c.reason, kind: k }); } catch (_) { /* optional */ }
            return;
        }
        const all = Object.assign({}, S.settings.gCal || {});
        all[k] = { ok: true, base: +c.base.toFixed(4), sign: c.sign, peak: +c.peak.toFixed(4), noise: +c.noise.toFixed(4) };
        S.saveSetting('gCal', all);
        buildDetector();
        setPhase('ready');
        E.gCalMsg.textContent = S.t('g_cal_done_' + k);
        status(S.t('g_ready_' + k), 'ok');
        try { window.__stage && window.__stage('gesture_calibrated', { kind: k }); } catch (_) { /* optional */ }
    }

    /* ── what the musician sees ──────────────────────────────────────── */

    function drawPreview(v, face) {
        const c = E.gPreview;
        const x = c.getContext('2d');
        const w = c.width, h = c.height;
        x.save();
        x.setTransform(-1, 0, 0, 1, w, 0);   // a mirror: turning right moves right
        // no frame this tick (an interrupted camera): the preview keeps the last one
        if (!window.__drawCameraFrame(x, v, 0, 0, w, h)) { x.restore(); return; }
        if (face && face.box) {
            x.strokeStyle = 'rgba(52,211,153,.9)'; x.lineWidth = 2;
            x.strokeRect(face.box.x * w, face.box.y * h, face.box.w * w, face.box.h * h);
        }
        x.restore();
    }

    function drawMeter() {
        const c = E.gMeter;
        const x = c.getContext('2d');
        const w = c.width, h = c.height;
        x.clearRect(0, 0, w, h);
        const cs = getComputedStyle(E.gPanel);
        x.fillStyle = cs.getPropertyValue('--ms-meter-bg').trim() || 'rgba(127,127,127,.18)';
        x.fillRect(0, h / 2 - 6, w, 12);
        const m = G.det ? G.det.meter() : null;
        const k = kind();
        const twoWay = KINDS[k].twoWay;
        const range = m ? m.threshold * 1.6 : 1;
        const mid = twoWay ? w / 2 : 12;
        const span = twoWay ? w / 2 - 10 : w - 24;
        const X = (v) => mid + Math.max(-1, Math.min(1, v / range)) * span;
        if (G.phase === 'calibrating' && G.samples) {
            // calibrating: the movement itself, around the rest seen so far
            const rest = G.samples.rest.filter(Number.isFinite).slice().sort((a, b) => a - b);
            const base = rest.length ? rest[rest.length >> 1] : NaN;
            const span0 = k === 'head' ? 30 : 0.8;
            const Xc = (v) => mid + Math.max(-1, Math.min(1, v / span0)) * span;
            if (Number.isFinite(G.raw) && Number.isFinite(base)) {
                x.fillStyle = '#2563eb';
                x.beginPath(); x.arc(Xc(G.raw - base), h / 2, 8, 0, Math.PI * 2); x.fill();
            }
        } else if (m) {
            x.fillStyle = 'rgba(52,211,153,.28)';
            x.fillRect(X(-m.release), h / 2 - 6, X(m.release) - X(-m.release), 12);
            x.fillStyle = 'rgba(37,99,235,.95)';
            x.fillRect(X(m.threshold) - 1.5, 4, 3, h - 8);
            if (twoWay) x.fillRect(X(-m.threshold) - 1.5, 4, 3, h - 8);
            x.fillStyle = Math.abs(m.value) >= m.threshold ? '#f59e0b' : '#10b981';
            if (G.on && G.phase === 'ready') { x.beginPath(); x.arc(X(m.value), h / 2, 8, 0, Math.PI * 2); x.fill(); }
        }
        E.gCount.textContent = G.fires ? S.t('g_count', { n: G.fires }) : '';
    }

    function onKindChange() {
        drawMeter();
        const has = buildDetector();
        if (G.on && G.phase === 'ready') {
            status(has ? S.t('g_ready_' + kind()) : S.t('g_need_calibration'), has ? 'ok' : 'warn');
            if (!has) startCalibration();
        }
    }

    G.enable = enable;
    G.disable = disable;
    G.onKindChange = onKindChange;
    G.drawMeter = drawMeter;
    G.stats = () => ({ fps: Math.round(G.fps), ms: Math.round(G.detectMs), fires: G.fires, phase: G.phase });
    setPhase('off');
    return G;
}
